// src/pages/Admin/Concierge.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../../../utils/cmsAxios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import SortableTable from "../../CommonComponents/SortableTable";
import ConciergeSearchBar from "./components/ConciergeSearchBar";
import { formatKstDateTime } from "../../../../utils/formatKstDateTime";

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const PAGE_BLOCK = 10;
const FILTER_KEYS = ["search_field", "keyword", "status", "date_field", "apply_start", "apply_end"];
const SORT_KEYS = ["created_at", "approved_at", "user_name", "phone", "store_name"];
const EMPTY_ITEMS = [];

const getPositivePage = (value) => {
  const page = Number(value || "1");
  return Number.isSafeInteger(page) && page > 0 && page <= 1000000 ? page : 1;
};

const getPageSize = (value) => {
  const pageSize = Number(value || DEFAULT_PAGE_SIZE);
  return PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE;
};

const ConciergeList = () => {
  const [listResult, setListResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshRevision, setRefreshRevision] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = useMemo(
    () => getPositivePage(searchParams.get("page")),
    [searchParams]
  );

  const pageSize = useMemo(
    () => getPageSize(searchParams.get("page_size")),
    [searchParams]
  );

  const sort = useMemo(
    () => ({
      key: SORT_KEYS.includes(searchParams.get("sort_key")) ? searchParams.get("sort_key") : null,
      dir: searchParams.get("sort_dir") === "desc" ? "desc" : "asc",
    }),
    [searchParams]
  );

  const filtersFromUrl = useMemo(() => {
    const filters = {};
    FILTER_KEYS.forEach((key) => {
      const value = searchParams.get(key);
      if (value) filters[key] = value;
    });
    return filters;
  }, [searchParams]);

  const updateListQuery = useCallback(
    (patch, { replace = false } = {}) => {
      const next = new URLSearchParams(searchParams);

      Object.entries(patch).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });

      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams]
  );

  const requestParams = useMemo(() => ({ ...filtersFromUrl, page: currentPage, page_size: pageSize,
    sort_key: sort.key || undefined, sort_dir: sort.dir }), [filtersFromUrl, currentPage, pageSize, sort]);
  const queryKey = JSON.stringify(requestParams);
  const listLoaded = listResult?.queryKey === queryKey;
  const items = listLoaded ? listResult.items : EMPTY_ITEMS;
  const total = listLoaded ? listResult.total : 0;
  const totalPages = listLoaded ? listResult.total_pages : Math.max(1, currentPage);
  const safeCurrentPage = listLoaded ? listResult.page : currentPage;
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setSelectedIds([]);
    axios.get(`${process.env.REACT_APP_FASTAPI_ADS_URL}/select/concierge/list`, {
      params: requestParams, signal: controller.signal,
    }).then(({ data }) => {
      if (!active) return;
      if (!Array.isArray(data.items) || !Number.isInteger(data.total) || !Number.isInteger(data.page)
        || !Number.isInteger(data.total_pages) || data.items.length > requestParams.page_size) {
        throw new Error("목록 페이징 응답을 확인할 수 없습니다. 서버 적용 상태를 확인해 주세요.");
      }
      setListResult({ ...data, queryKey });
    }).catch((err) => {
      if (!active) return;
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err?.message || "컨시어지 신청 리스트 조회 중 오류가 발생했습니다.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [requestParams, queryKey, refreshRevision]);

  const handleSearchFilters = useCallback((filters) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      FILTER_KEYS.forEach((key) => {
        const value = filters?.[key];
        if (value === undefined || value === null || value === "") {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
      next.set("page", "1");
      return next;
    });
    setRefreshRevision((value) => value + 1);
  }, [setSearchParams]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  }, []);

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      alert("삭제할 신청을 선택해 주세요.");
      return;
    }

    if (!window.confirm(`선택한 ${selectedIds.length}건을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      setDeleting(true);

      await axios.post(
        `${process.env.REACT_APP_FASTAPI_ADS_URL}/concierge/delete`,
        { ids: selectedIds }
      );

      setSelectedIds([]);
      setRefreshRevision((value) => value + 1);
    } catch (err) {
      console.error("[admin/concierge/delete] error:", err);
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "컨시어지 신청 삭제 중 오류가 발생했습니다."
      );
    } finally {
      setDeleting(false);
    }
  };

  const renderStatusBadge = useCallback((v) => {
    const base = "inline-flex items-center px-2 py-1 rounded-full text-xs";
    if (v === "APPROVED") {
      return <span className={`${base} bg-emerald-50 text-emerald-700`}>승인</span>;
    }
    if (v === "STOPPED") {
      return <span className={`${base} bg-neutral-100 text-neutral-700`}>종료</span>;
    }
    if (v === "PAUSED") {
      return <span className={`${base} bg-blue-50 text-blue-700`}>일시정지</span>;
    }
    return <span className={`${base} bg-amber-50 text-amber-700`}>대기</span>;
  }, []);

  useEffect(() => {
    if (listLoaded && currentPage !== safeCurrentPage) {
      updateListQuery({ page: safeCurrentPage }, { replace: true });
    }
  }, [currentPage, listLoaded, safeCurrentPage, updateListQuery]);

  const blockStart = useMemo(
    () => Math.floor((safeCurrentPage - 1) / PAGE_BLOCK) * PAGE_BLOCK + 1,
    [safeCurrentPage]
  );

  const blockEnd = useMemo(
    () => Math.min(blockStart + PAGE_BLOCK - 1, totalPages),
    [blockStart, totalPages]
  );

  const columns = useMemo(
    () => [
      {
        key: "_select",
        headerRender: () => (
          <input
            type="checkbox"
            aria-label="현재 페이지 전체 선택"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedIds(items.map((item) => item.id));
              } else {
                setSelectedIds([]);
              }
            }}
            checked={items.length > 0 && selectedIds.length === items.length}
          />
        ),
        sortable: false,
        cellClassName: "w-10 align-middle",
        render: (_value, row) => (
          <input
            type="checkbox"
            aria-label={`${row.store_name} 선택`}
            checked={selectedIds.includes(row.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleSelect(row.id)}
          />
        ),
      },
      {
        key: "created_at",
        header: "신청일",
        type: "date",
        cellClassName: "text-xs text-neutral-500 align-middle",
        render: (v) => formatKstDateTime(v),
      },
      {
        key: "approved_at",
        header: "승인일",
        type: "date",
        cellClassName: "text-xs text-neutral-500 align-middle",
        render: (v) => formatKstDateTime(v),
      },
      {
        key: "user_name",
        header: "이름",
        cellClassName: "align-top",
        render: (v) => <div className="text-sm text-neutral-900">{v}</div>,
      },
      {
        key: "phone",
        header: "전화",
        cellClassName: "text-sm text-neutral-700 align-top",
      },
      {
        key: "store_name",
        header: "매장명",
        cellClassName: "text-sm text-neutral-900 align-top",
      },
      {
        key: "road_name",
        header: "도로명 주소",
        sortable: false,
        cellClassName: "text-sm text-neutral-700 align-top",
      },
      {
        key: "status",
        header: "상태",
        sortable: false,
        cellClassName: "align-top",
        render: (v) => renderStatusBadge(v),
      },
    ],
    [items, renderStatusBadge, selectedIds, toggleSelect]
  );

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto max-w-screen-2xl bg-white rounded-2xl shadow mt-6">
        <div className="flex">
          <div className="flex-1 min-w-0 bg-neutral-50/70">
            <main className="pt-6 flex flex-col p-4 w-full">
              <ConciergeSearchBar
                title="컨시어지 목록"
                onSearch={handleSearchFilters}
                fetchList={() => setRefreshRevision((value) => value + 1)}
                initialFilters={filtersFromUrl}
                itemsCount={total}
              />

              <section className="px-4 py-3 w-full overflow-x-hidden flex justify-between items-center">
                <div className="text-xs text-neutral-500">
                  총 <span className="font-semibold">{total}</span>건
                </div>
                <div className="flex items-center gap-2">
                  <label htmlFor="concierge-page-size" className="text-xs text-neutral-500">
                    목록 수
                  </label>
                  <select
                    id="concierge-page-size"
                    value={pageSize}
                    onChange={(event) =>
                      updateListQuery({ page_size: event.target.value, page: 1 })
                    }
                    className="h-9 rounded-lg border border-neutral-300 bg-white px-2 text-xs text-neutral-800"
                  >
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}개
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleDeleteSelected}
                    disabled={loading || deleting}
                    className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
                  >
                    선택 삭제
                  </button>
                </div>
              </section>

              <section className="px-4 pb-4 w-full overflow-x-auto bg-white">
                {error && (
                  <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}

                {loading ? (
                  <div className="py-10 text-center text-sm text-neutral-500">
                    로딩 중입니다...
                  </div>
                ) : items.length === 0 ? (
                  <div className="py-10 text-center text-sm text-neutral-500">
                    조회된 컨시어지 신청이 없습니다.
                  </div>
                ) : (
                  <>
                    <SortableTable
                      data={items}
                      manualSorting
                      columns={columns}
                      sort={sort}
                      onSortChange={(nextSort) => {
                        updateListQuery({
                          sort_key: nextSort?.key || null,
                          sort_dir: nextSort?.key ? nextSort.dir : null,
                          page: 1,
                        });
                      }}
                      rowKey="id"
                      tableClassName="min-w-full text-sm border-t border-neutral-200"
                      headClassName="bg-neutral-50"
                      bodyClassName="divide-y divide-neutral-100"
                      bodyCellClassName="whitespace-nowrap"
                      onRowClick={(row) => {
                        const returnTo = `${location.pathname}${location.search}`;
                        sessionStorage.setItem("concierge_list_return_to", returnTo);
                        navigate(
                          `/concierge/${row.id}?return_to=${encodeURIComponent(returnTo)}`,
                          { state: { returnTo } }
                        );
                      }}
                    />

                    {totalPages > 1 && (
                      <div className="mt-4 flex justify-center items-center gap-2 text-xs text-neutral-700">
                        <button
                          type="button"
                          onClick={() => updateListQuery({ page: Math.max(1, blockStart - PAGE_BLOCK) })}
                          disabled={blockStart === 1}
                          className="px-2 py-1 border rounded disabled:opacity-40"
                        >
                          이전
                        </button>

                        <div className="flex items-center gap-1">
                          {Array.from({ length: blockEnd - blockStart + 1 }).map((_, idx) => {
                            const page = blockStart + idx;
                            return (
                              <button
                                key={page}
                                type="button"
                                onClick={() => updateListQuery({ page })}
                                className={
                                  "min-w-[28px] px-2 py-1 border rounded text-xs " +
                                  (page === safeCurrentPage
                                    ? "bg-neutral-900 text-white"
                                    : "bg-white text-neutral-800 hover:bg-neutral-100")
                                }
                              >
                                {page}
                              </button>
                            );
                          })}
                        </div>

                        <button
                          type="button"
                          onClick={() => updateListQuery({ page: Math.min(totalPages, blockStart + PAGE_BLOCK) })}
                          disabled={blockEnd === totalPages}
                          className="px-2 py-1 border rounded disabled:opacity-40"
                        >
                          다음
                        </button>
                      </div>
                    )}
                  </>
                )}
              </section>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConciergeList;
